import { ko } from 'date-fns/locale/ko';
import moment from 'moment';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { getMAJAB010 } from '../../../services/Api';
import SideMenu from '../../../shared/components/SideMenu';
import { MENU_STATISTICAL } from '../../../shared/constants/app';
registerLocale('ko', ko);

// (합계) 행은 항상 표시, 일반 상품 행은 모든 값이 0이면 숨김.
const filterZeroRows = (list, keys) =>
    list.filter(
        item => item.productName?.includes('(합계)') || keys.some(key => Number(item[key] || 0) !== 0)
    );

const TRANSACTION_KEYS = [
    'inicisPayment', 'inicisPaymentAmount', 'inicisVatExcluded',
    'ticketPayment', 'ticketPaymentAmount', 'ticketVatExcluded',
    'totalPayment', 'totalPaymentAmount', 'totalVatExcluded',
];
const PURCHASE_KEYS = [
    'totalPurchase', 'totalPurchaseAmount', 'totalPurchaseVatExcluded',
    'used', 'usedAmount', 'usedVatExcluded',
    'unused', 'unusedAmount', 'unusedVatExcluded',
    'refund', 'refundAmount', 'refundVatExcluded',
];
const SALES_KEYS = [
    'totalPurchaseCount', 'totalPurchaseAmount', 'totalPurchaseTaxExcluded',
    'usedCount', 'usedAmount', 'usedTaxExcluded',
    'unusedCount', 'unusedAmount', 'unusedTaxExcluded',
    'refundCount', 'refundAmount', 'refundTaxExcluded',
];
const PAYMENT_KEYS = [
    'inicisPayment', 'inicisPaymentAmount', 'inicisVATExcluded',
    'ticketPayment', 'ticketPaymentAmount', 'ticketVATExcluded',
    'totalPayment', 'totalPaymentAmount', 'totalPaymentVATExcluded',
];

const MAJAB010 = () => {
    const today = moment().toDate();
    const [date, setDate] = useState(moment().subtract(1, 'day').toDate());
    const [transactionSummaries, setTransactionSummaries] = useState([]);
    const [sumTransactionSummaries, setSumTransactionSummaries] = useState({
        ticketPaymentAmount: 0,
        ticketPayment: 0,
        ticketVatExcluded: 0,
        inicisPaymentAmount: 0,
        totalPaymentAmount: 0,
        inicisPayment: 0,
        inicisVatExcluded: 0,
        totalPayment: 0,
        totalVatExcluded: 0,
    });

    const [purchaseSummaries, setPurchaseSummaries] = useState([]);
    const [sumPurchaseSummaries, setSumPurchaseSummaries] = useState({
        totalPurchase: 0,
        totalPurchaseAmount: 0,
        totalPurchaseVatExcluded: 0,
        used: 0,
        usedAmount: 0,
        usedVatExcluded: 0,
        unused: 0,
        unusedAmount: 0,
        unusedVatExcluded: 0,
        refund: 0,
        refundAmount: 0,
        refundVatExcluded: 0,
    });
    const [salesSummaries, setSalesSummaries] = useState([]);
    const [sumSalesSummaries, setSumSalesSummaries] = useState({
        totalPurchaseAmount: 0,
        totalPurchaseCount: 0,
        usedAmount: 0,
        usedCount: 0,
        refundAmount: 0,
        refundTaxExcluded: 0,
        unusedAmount: 0,
        usedTaxExcluded: 0,
        refundCount: 0,
        unusedCount: 0,
        totalPurchaseTaxExcluded: 0,
        unusedTaxExcluded: 0,
    });
    const [couponUsageSummaries, setCouponUsageSummaries] = useState([]);
    const [couponSummaries, setCouponSummaries] = useState([]);
    const [paymentSummaries, setPaymentSummaries] = useState([]);
    const [sumPaymentSummaries, setSumPaymentSummaries] = useState({
        ticketPaymentAmount: 0,
        ticketPayment: 0,
        ticketVATExcluded: 0,
        inicisPaymentAmount: 0,
        totalPaymentAmount: 0,
        depositDate: 0,
        inicisPayment: 0,
        inicisVATExcluded: 0,
        totalPayment: 0,
        totalPaymentVATExcluded: 0,
    });

    // 상품명 컬럼 너비를 첫 번째 테이블(결제내역 일계) 기준으로 통일
    const productNameRef = useRef(null);
    const [productNameWidth, setProductNameWidth] = useState(null);
    const productNameStyle = productNameWidth
        ? { boxSizing: 'border-box', width: productNameWidth, minWidth: productNameWidth, maxWidth: productNameWidth }
        : undefined;

    useLayoutEffect(() => {
        const updateWidth = () => {
            if (productNameRef.current) {
                setProductNameWidth(productNameRef.current.getBoundingClientRect().width);
            }
        };
        updateWidth();
        window.addEventListener('resize', updateWidth);
        return () => window.removeEventListener('resize', updateWidth);
    }, [transactionSummaries]);

    const handleDateChange = (date) => {
        const dateFormat = date ? moment(date).format('YYYY/MM/DD') : moment().subtract(1, 'day').format('YYYY/MM/DD');
        setDate(dateFormat);
    };

    useEffect(() => {
        const dateFormat = date ? moment(date).format('YYYYMMDD') : null;
        getMAJAB010(dateFormat)
            .then((data) => {
                setTransactionSummaries(data.data.transactionSummaries);
                setPurchaseSummaries(data.data.purchaseSummaries);
                setSalesSummaries(data.data.salesSummaries);
                setCouponUsageSummaries(data.data.couponUsageSummaries);
                setCouponSummaries(data.data.couponSummaries);
                setPaymentSummaries(data.data.paymentSummaries);

                setSumTransactionSummaries(
                    data.data.transactionSummaries &&
                        data.data.transactionSummaries.length > 0
                        ? data.data.transactionSummaries.reduce(
                            (acc, item) => {
                                if (!item.productName?.includes('(합계)')) {
                                    acc.ticketPaymentAmount += item.ticketPaymentAmount || 0;
                                    acc.ticketPayment += item.ticketPayment || 0;
                                    acc.ticketVatExcluded += item.ticketVatExcluded || 0;

                                    acc.inicisPaymentAmount += item.inicisPaymentAmount || 0;
                                    acc.inicisPayment += item.inicisPayment || 0;
                                    acc.inicisVatExcluded += item.inicisVatExcluded || 0;

                                    acc.totalPaymentAmount += item.totalPaymentAmount || 0;
                                    acc.totalPayment += item.totalPayment || 0;
                                    acc.totalVatExcluded += item.totalVatExcluded || 0;
                                }
                                return acc;
                            },
                            {
                                ticketPaymentAmount: 0,
                                ticketPayment: 0,
                                ticketVatExcluded: 0,
                                inicisPaymentAmount: 0,
                                inicisPayment: 0,
                                inicisVatExcluded: 0,
                                totalPaymentAmount: 0,
                                totalPayment: 0,
                                totalVatExcluded: 0,
                            }
                        )
                        : null
                );


                setSumPurchaseSummaries(
                    data.data.purchaseSummaries &&
                        data.data.purchaseSummaries.length > 0
                        ? data.data.purchaseSummaries.reduce(
                            (acc, item) => {
                                if (!item.productName?.includes('(합계)')) {
                                    acc.totalPurchase += item.totalPurchase || 0;
                                    acc.totalPurchaseAmount += item.totalPurchaseAmount || 0;
                                    acc.totalPurchaseVatExcluded += item.totalPurchaseVatExcluded || 0;
                                    acc.used += item.used || 0;
                                    acc.usedAmount += item.usedAmount || 0;
                                    acc.usedVatExcluded += item.usedVatExcluded || 0;
                                    acc.unused += item.unused || 0;
                                    acc.unusedAmount += item.unusedAmount || 0;
                                    acc.unusedVatExcluded += item.unusedVatExcluded || 0;
                                    acc.refund += item.refund || 0;
                                    acc.refundAmount += item.refundAmount || 0;
                                    acc.refundVatExcluded += item.refundVatExcluded || 0;
                                }
                                return acc;
                            },
                            {
                                totalPurchase: 0,
                                totalPurchaseAmount: 0,
                                totalPurchaseVatExcluded: 0,
                                used: 0,
                                usedAmount: 0,
                                usedVatExcluded: 0,
                                unused: 0,
                                unusedAmount: 0,
                                unusedVatExcluded: 0,
                                refund: 0,
                                refundAmount: 0,
                                refundVatExcluded: 0,
                            }
                        )
                        : null
                );


                setSumSalesSummaries(
                    data.data.salesSummaries &&
                        data.data.salesSummaries.length > 0
                        ? data.data.salesSummaries.reduce(
                            (acc, item) => {
                                if (!item.productName?.includes('(합계)')) {
                                    acc.totalPurchaseAmount += item.totalPurchaseAmount || 0;
                                    acc.totalPurchaseCount += item.totalPurchaseCount || 0;
                                    acc.usedAmount += item.usedAmount || 0;
                                    acc.usedCount += item.usedCount || 0;
                                    acc.refundAmount += item.refundAmount || 0;
                                    acc.refundTaxExcluded += item.refundTaxExcluded || 0;
                                    acc.unusedAmount += item.unusedAmount || 0;
                                    acc.usedTaxExcluded += item.usedTaxExcluded || 0;
                                    acc.refundCount += item.refundCount || 0;
                                    acc.unusedCount += item.unusedCount || 0;
                                    acc.totalPurchaseTaxExcluded += item.totalPurchaseTaxExcluded || 0;
                                    acc.unusedTaxExcluded += item.unusedTaxExcluded || 0;
                                }
                                return acc;
                            },
                            {
                                totalPurchaseAmount: 0,
                                totalPurchaseCount: 0,
                                usedAmount: 0,
                                usedCount: 0,
                                refundAmount: 0,
                                refundTaxExcluded: 0,
                                unusedAmount: 0,
                                usedTaxExcluded: 0,
                                refundCount: 0,
                                unusedCount: 0,
                                totalPurchaseTaxExcluded: 0,
                                unusedTaxExcluded: 0,
                            }
                        )
                        : null
                );


                setSumPaymentSummaries(
                    data.data.paymentSummaries &&
                        data.data.paymentSummaries.length > 0
                        ? data.data.paymentSummaries.reduce(
                            (acc, item) => {
                                if (!item.productName?.includes('(합계)')) {
                                    acc.ticketPaymentAmount += item.ticketPaymentAmount || 0;
                                    acc.ticketPayment += item.ticketPayment || 0;
                                    acc.ticketVATExcluded += item.ticketVATExcluded || 0;
                                    acc.inicisPaymentAmount += item.inicisPaymentAmount || 0;
                                    acc.totalPaymentAmount += item.totalPaymentAmount || 0;
                                    acc.inicisPayment += item.inicisPayment || 0;
                                    acc.inicisVATExcluded += item.inicisVATExcluded || 0;
                                    acc.totalPayment += item.totalPayment || 0;
                                    acc.totalPaymentVATExcluded += item.totalPaymentVATExcluded || 0;
                                }
                                return acc;
                            },
                            {
                                ticketPaymentAmount: 0,
                                ticketPayment: 0,
                                ticketVATExcluded: 0,
                                inicisPaymentAmount: 0,
                                totalPaymentAmount: 0,
                                inicisPayment: 0,
                                inicisVATExcluded: 0,
                                totalPayment: 0,
                                totalPaymentVATExcluded: 0,
                            }
                        )
                        : null
                );

            })
            .catch((error) => console.log(error));
    }, [date]);

    return (
        <div className="statis-page">
            <div className="container-fluid container">
                <div className="content wrap">
                    <SideMenu menu={MENU_STATISTICAL} />
                    <div className="content-right custom-count-item">
                        <div className="title">일별등록</div>
                        <div>
                            <table className="table-gray">
                                <tbody>
                                    <tr>
                                        <th style={{ width: 150 }}>일자</th>
                                        <td colSpan={3} style={{ textAlign: 'left' }}>
                                            <DatePicker
                                                style={{ width: 200 }}
                                                onChange={(date) => handleDateChange(date, 'date')}
                                                locale="ko"
                                                placeholderText="YYYY/MM/DD"
                                                dateFormat="yyyy/MM/dd"
                                                selected={date}
                                                maxDate={today}
                                                showYearDropdown
                                                showMonthDropdown
                                                scrollableYearDropdown
                                            />
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div className="statistics-by-day results">
                            <div className="count-item">
                                [ 덴탈잡 결제내역 일계 - 이용권 결제내역은 구매일이 아닌 사용일(조회일) 기준입니다.]
                            </div>
                            <table className="table-gray table-daily-registration">
                                <thead>
                                    <tr>
                                        <th ref={productNameRef}>상품명</th>
                                        <th>할인구분</th>
                                        <th>단가</th>
                                        <th>이니시스결제</th>
                                        <th>이니시스결제금액</th>
                                        <th>이니시스부가세별도</th>
                                        <th>이용권결제</th>
                                        <th>이용권결제금액</th>
                                        <th>이용권부가세별도</th>
                                        <th>결제합계</th>
                                        <th>결제합계금액</th>
                                        <th>결제합계부가세별도</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transactionSummaries &&
                                        transactionSummaries.length > 0 &&
                                        filterZeroRows(transactionSummaries, TRANSACTION_KEYS).map((item, index) => {
                                            return (
                                                <tr
                                                    className={item.productName.includes('(합계)') ? 'sum-row' : ''}
                                                    key={`transactionSummaries${index}`}
                                                >
                                                    <td>{item.productName}</td>
                                                    <td>{item.discountType}</td>
                                                    <td>{item.unitPrice > 0 ? item.unitPrice.toLocaleString() : (!item.productName?.includes('(합계)') ? '0' : '')}</td>

                                                    <td>
                                                        {item.inicisPayment.toLocaleString() !== '0' &&
                                                            item.inicisPayment.toLocaleString()}
                                                    </td>
                                                    <td>
                                                        {item.inicisPaymentAmount.toLocaleString() !== '0' &&
                                                            item.inicisPaymentAmount.toLocaleString()}
                                                    </td>
                                                    <td>
                                                        {item.inicisVatExcluded.toLocaleString() !== '0' &&
                                                            item.inicisVatExcluded.toLocaleString()}
                                                    </td>
                                                    <td>
                                                        {item.ticketPayment.toLocaleString() !== '0' &&
                                                            item.ticketPayment.toLocaleString()}
                                                    </td>
                                                    <td>
                                                        {item.ticketPaymentAmount.toLocaleString() !== '0' &&
                                                            item.ticketPaymentAmount.toLocaleString()}
                                                    </td>
                                                    <td>
                                                        {item.ticketVatExcluded.toLocaleString() !== '0' &&
                                                            item.ticketVatExcluded.toLocaleString()}
                                                    </td>
                                                    <td>
                                                        {item.totalPayment.toLocaleString() !== '0' &&
                                                            item.totalPayment.toLocaleString()}
                                                    </td>
                                                    <td>
                                                        {item.totalPaymentAmount.toLocaleString() !== '0' &&
                                                            item.totalPaymentAmount.toLocaleString()}
                                                    </td>
                                                    <td>
                                                        {item.totalVatExcluded.toLocaleString() !== '0' &&
                                                            item.totalVatExcluded.toLocaleString()}
                                                        <br />({item.totalVatExcluded})
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    {transactionSummaries &&
                                        transactionSummaries.length > 0 &&
                                        sumTransactionSummaries ? (
                                        <tr className="sum-all">
                                            <td>총계</td>
                                            <td></td>
                                            <td></td>

                                            {/* inicisPayment */}
                                            <td>
                                                {sumTransactionSummaries.inicisPayment > 0 &&
                                                    sumTransactionSummaries.inicisPayment.toLocaleString()}
                                            </td>

                                            {/* inicisPaymentAmount */}
                                            <td>
                                                {sumTransactionSummaries.inicisPaymentAmount > 0 &&
                                                    sumTransactionSummaries.inicisPaymentAmount.toLocaleString()}
                                            </td>

                                            {/* inicisVatExcluded */}
                                            <td>
                                                {sumTransactionSummaries.inicisVatExcluded > 0 &&
                                                    sumTransactionSummaries.inicisVatExcluded.toLocaleString()}
                                            </td>

                                            {/* ticketPayment */}
                                            <td>
                                                {sumTransactionSummaries.ticketPayment > 0 &&
                                                    sumTransactionSummaries.ticketPayment.toLocaleString()}
                                            </td>

                                            {/* ticketPaymentAmount */}
                                            <td>
                                                {sumTransactionSummaries.ticketPaymentAmount > 0 &&
                                                    sumTransactionSummaries.ticketPaymentAmount.toLocaleString()}
                                            </td>

                                            {/* ticketVatExcluded */}
                                            <td>
                                                {sumTransactionSummaries.ticketVatExcluded > 0 &&
                                                    sumTransactionSummaries.ticketVatExcluded.toLocaleString()}
                                            </td>

                                            {/* totalPayment */}
                                            <td>
                                                {sumTransactionSummaries.totalPayment > 0 &&
                                                    sumTransactionSummaries.totalPayment.toLocaleString()}
                                            </td>

                                            {/* totalPaymentAmount */}
                                            <td>
                                                {sumTransactionSummaries.totalPaymentAmount > 0 &&
                                                    sumTransactionSummaries.totalPaymentAmount.toLocaleString()}
                                            </td>

                                            {/* totalVatExcluded */}
                                            <td>
                                                {sumTransactionSummaries.totalVatExcluded > 0 &&
                                                    sumTransactionSummaries.totalVatExcluded.toLocaleString()}
                                                <br />({sumTransactionSummaries.totalVatExcluded})
                                            </td>
                                        </tr>

                                    ) : (
                                        <></>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <div className="purchaseSummaries results">
                            <div className="count-item">[ 덴탈잡 일반기업배너 신청 리스트 ]</div>
                            <div className="count-item">[ 덴탈잡 이용권 일계 - 구매일(조회일) 기준입니다. ]</div>
                            <table className="table-gray table-statis table-daily-registration">
                                <thead>
                                    <tr>
                                        <th className="blue" style={productNameStyle}>상품명</th>
                                        <th>할인구분</th>
                                        <th>단가</th>
                                        <th className="blue">총구매</th>
                                        <th>총구매금액</th>
                                        <th>총구매부가세별도</th>
                                        <th className="blue">사용</th>
                                        <th>사용금액</th>
                                        <th>사용부가세별도</th>
                                        <th className="blue">미사용</th>
                                        <th>미사용금액</th>
                                        <th className="blue">미사용부가세별도</th>
                                        <th>환불</th>
                                        <th>환불금액</th>
                                        <th>환불부가세별도</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {purchaseSummaries &&
                                        purchaseSummaries.length > 0 &&
                                        filterZeroRows(purchaseSummaries, PURCHASE_KEYS).map((item, index) => {
                                            return (
                                                <tr
                                                    className={item.productName.includes('(합계)') ? 'sum-row' : ''}
                                                    key={`purchaseSummaries${index}`}
                                                >
                                                    <td className={item.productName.includes('(합계)') ? 'blue' : ''}>
                                                        {item.productName}
                                                    </td>
                                                    <td>{item.discountType.toLocaleString()}</td>
                                                    <td>{item.unitPrice > 0 ? item.unitPrice.toLocaleString() : (!item.productName?.includes('(합계)') ? '0' : '')}</td>

                                                    <td className={item.productName.includes('(합계)') ? 'blue' : ''}>
                                                        {item.totalPurchase.toLocaleString() !== '0' &&
                                                            item.totalPurchase.toLocaleString()}
                                                    </td>
                                                    <td>
                                                        {item.totalPurchaseAmount.toLocaleString() !== '0' &&
                                                            item.totalPurchaseAmount.toLocaleString()}
                                                    </td>
                                                    <td>
                                                        {item.totalPurchaseVatExcluded.toLocaleString() !== '0' &&
                                                            item.totalPurchaseVatExcluded.toLocaleString()}
                                                    </td>
                                                    <td className={item.productName.includes('(합계)') ? 'blue' : ''}>
                                                        {item.used.toLocaleString() !== '0' &&
                                                            item.used.toLocaleString()}
                                                    </td>
                                                    <td>
                                                        {item.usedAmount.toLocaleString() !== '0' &&
                                                            item.usedAmount.toLocaleString()}
                                                    </td>
                                                    <td>
                                                        {item.usedVatExcluded.toLocaleString() !== '0' &&
                                                            item.usedVatExcluded.toLocaleString()}
                                                    </td>
                                                    <td className={item.productName.includes('(합계)') ? 'blue' : ''}>
                                                        {item.unused.toLocaleString() !== '0' &&
                                                            item.unused.toLocaleString()}
                                                    </td>
                                                    <td>
                                                        {item.unusedAmount.toLocaleString() !== '0' &&
                                                            item.unusedAmount.toLocaleString()}
                                                    </td>
                                                    <td className={item.productName.includes('(합계)') ? 'blue' : ''}>
                                                        {item.unusedVatExcluded.toLocaleString() !== '0' &&
                                                            item.unusedVatExcluded.toLocaleString()}
                                                        <br />({item.unusedVatExcluded})
                                                    </td>
                                                    <td>
                                                        {item.refund.toLocaleString() !== '0' &&
                                                            item.refund.toLocaleString()}
                                                    </td>
                                                    <td>
                                                        {item.refundAmount.toLocaleString() !== '0' &&
                                                            item.refundAmount.toLocaleString()}
                                                    </td>
                                                    <td>
                                                        {item.refundVatExcluded.toLocaleString() !== '0' &&
                                                            item.refundVatExcluded.toLocaleString()}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    {purchaseSummaries && purchaseSummaries.length > 0 && sumPurchaseSummaries && (
                                        <tr className="sum-all">
                                            <td>총계</td>
                                            <td></td>
                                            <td></td>
                                            <td className="blue">
                                                {sumPurchaseSummaries.totalPurchase.toLocaleString() !== '0' &&
                                                    sumPurchaseSummaries.totalPurchase.toLocaleString()}
                                            </td>
                                            <td>
                                                {sumPurchaseSummaries.totalPurchaseAmount.toLocaleString() !== '0' &&
                                                    sumPurchaseSummaries.totalPurchaseAmount.toLocaleString()}
                                            </td>
                                            <td>
                                                {sumPurchaseSummaries.totalPurchaseVatExcluded.toLocaleString() !==
                                                    '0' &&
                                                    sumPurchaseSummaries.totalPurchaseVatExcluded.toLocaleString()}
                                            </td>
                                            <td className="blue">
                                                {sumPurchaseSummaries.used.toLocaleString() !== '0' &&
                                                    sumPurchaseSummaries.used.toLocaleString()}
                                            </td>
                                            <td>
                                                {sumPurchaseSummaries.usedAmount.toLocaleString() !== '0' &&
                                                    sumPurchaseSummaries.usedAmount.toLocaleString()}
                                            </td>
                                            <td>
                                                {sumPurchaseSummaries.usedVatExcluded.toLocaleString() !== '0' &&
                                                    sumPurchaseSummaries.usedVatExcluded.toLocaleString()}
                                            </td>
                                            <td className="blue">
                                                {sumPurchaseSummaries.unused.toLocaleString() !== '0' &&
                                                    sumPurchaseSummaries.unused.toLocaleString()}
                                            </td>
                                            <td>
                                                {sumPurchaseSummaries.unusedAmount.toLocaleString() !== '0' &&
                                                    sumPurchaseSummaries.unusedAmount.toLocaleString()}
                                            </td>
                                            <td className="blue">
                                                {sumPurchaseSummaries.unusedVatExcluded.toLocaleString() !== '0' &&
                                                    sumPurchaseSummaries.unusedVatExcluded.toLocaleString()}
                                                <br />({sumPurchaseSummaries.unusedVatExcluded})
                                            </td>
                                            <td>
                                                {sumPurchaseSummaries.refund !== '0' &&
                                                    sumPurchaseSummaries.refund.toLocaleString()}
                                            </td>
                                            <td>
                                                {sumPurchaseSummaries.refundAmount.toLocaleString() !== '0' &&
                                                    sumPurchaseSummaries.refundAmount.toLocaleString()}
                                            </td>
                                            <td>
                                                {sumPurchaseSummaries.refundVatExcluded.toLocaleString() !== '0' &&
                                                    sumPurchaseSummaries.refundVatExcluded.toLocaleString()}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <div className="salesSummaries results">
                            <div className="count-item">[ 덴탈잡 이용권 월계 ]</div>
                            <table className="table-gray table-statis table-daily-registration">
                                <thead>
                                    <tr>
                                        <th className="blue" style={productNameStyle}>상품명</th>
                                        <th>할인구분</th>
                                        <th>단가</th>
                                        <th className="blue">총구매</th>
                                        <th>총구매금액</th>
                                        <th>총구매부가세별도</th>
                                        <th className="blue">사용</th>
                                        <th>사용금액</th>
                                        <th>사용부가세별도</th>
                                        <th className="blue">미사용</th>
                                        <th>미사용금액</th>
                                        <th className="blue">미사용부가세별도</th>
                                        <th>환불</th>
                                        <th>환불금액</th>
                                        <th>환불부가세별도</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {salesSummaries &&
                                        salesSummaries.length > 0 &&
                                        filterZeroRows(salesSummaries, SALES_KEYS).map((item, index) => {
                                            return (
                                                <tr
                                                    className={item.productName.includes('(합계)') ? 'sum-row' : ''}
                                                    key={`salesSummaries${index}`}
                                                >
                                                    <td className={item.productName.includes('(합계)') ? 'blue' : ''}>
                                                        {item.productName}
                                                    </td>
                                                    <td>{item.discountType}</td>
                                                    <td>{item.unitPrice > 0 ? item.unitPrice.toLocaleString() : (!item.productName?.includes('(합계)') ? '0' : '')}</td>

                                                    <td className={item.productName.includes('(합계)') ? 'blue' : ''}>
                                                        {item.totalPurchaseCount.toLocaleString() !== '0' &&
                                                            item.totalPurchaseCount.toLocaleString()}
                                                    </td>
                                                    <td>
                                                        {item.totalPurchaseAmount.toLocaleString() !== '0' &&
                                                            item.totalPurchaseAmount.toLocaleString()}
                                                    </td>
                                                    <td>
                                                        {item.totalPurchaseTaxExcluded.toLocaleString() !== '0' &&
                                                            item.totalPurchaseTaxExcluded.toLocaleString()}
                                                    </td>
                                                    <td className={item.productName.includes('(합계)') ? 'blue' : ''}>
                                                        {item.usedCount.toLocaleString() !== '0' &&
                                                            item.usedCount.toLocaleString()}
                                                    </td>
                                                    <td>
                                                        {item.usedAmount.toLocaleString() !== '0' &&
                                                            item.usedAmount.toLocaleString()}
                                                    </td>
                                                    <td>
                                                        {item.usedTaxExcluded.toLocaleString() !== '0' &&
                                                            item.usedTaxExcluded.toLocaleString()}
                                                    </td>
                                                    <td className={item.productName.includes('(합계)') ? 'blue' : ''}>
                                                        {item.unusedCount.toLocaleString() !== '0' &&
                                                            item.unusedCount.toLocaleString()}
                                                    </td>
                                                    <td>
                                                        {item.unusedAmount.toLocaleString() !== '0' &&
                                                            item.unusedAmount.toLocaleString()}
                                                    </td>
                                                    <td className={item.productName.includes('(합계)') ? 'blue' : ''}>
                                                        {item.unusedTaxExcluded.toLocaleString() !== '0' &&
                                                            item.unusedTaxExcluded.toLocaleString()}
                                                        <br />({item.unusedTaxExcluded})
                                                    </td>
                                                    <td>
                                                        {item.refundCount.toLocaleString() !== '0' &&
                                                            item.refundCount.toLocaleString()}
                                                    </td>
                                                    <td>
                                                        {item.refundAmount.toLocaleString() !== '0' &&
                                                            item.refundAmount.toLocaleString()}
                                                    </td>
                                                    <td>
                                                        {item.refundTaxExcluded.toLocaleString() !== '0' &&
                                                            item.refundTaxExcluded.toLocaleString()}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    {salesSummaries && salesSummaries.length > 0 && sumSalesSummaries && (
                                        <tr className="sum-all">
                                            <td>총계</td>
                                            <td></td>
                                            <td></td>
                                            <td className="blue">
                                                {sumSalesSummaries.totalPurchaseCount.toLocaleString() !== '0' &&
                                                    sumSalesSummaries.totalPurchaseCount.toLocaleString()}
                                            </td>
                                            <td>
                                                {sumSalesSummaries.totalPurchaseAmount.toLocaleString() !== '0' &&
                                                    sumSalesSummaries.totalPurchaseAmount.toLocaleString()}
                                            </td>
                                            <td>
                                                {sumSalesSummaries.totalPurchaseTaxExcluded.toLocaleString() !== '0' &&
                                                    sumSalesSummaries.totalPurchaseTaxExcluded.toLocaleString()}
                                            </td>
                                            <td className="blue">
                                                {sumSalesSummaries.usedCount.toLocaleString() !== '0' &&
                                                    sumSalesSummaries.usedCount.toLocaleString()}
                                            </td>
                                            <td>
                                                {sumSalesSummaries.usedAmount.toLocaleString() !== '0' &&
                                                    sumSalesSummaries.usedAmount.toLocaleString()}
                                            </td>
                                            <td>
                                                {sumSalesSummaries.usedTaxExcluded.toLocaleString() !== '0' &&
                                                    sumSalesSummaries.usedTaxExcluded.toLocaleString()}
                                            </td>
                                            <td className="blue">
                                                {sumSalesSummaries.unusedCount.toLocaleString() !== '0' &&
                                                    sumSalesSummaries.unusedCount.toLocaleString()}
                                            </td>
                                            <td>
                                                {sumSalesSummaries.unusedAmount.toLocaleString() !== '0' &&
                                                    sumSalesSummaries.unusedAmount.toLocaleString()}
                                            </td>
                                            <td className="blue">
                                                {sumSalesSummaries.unusedTaxExcluded.toLocaleString() !== '0' &&
                                                    sumSalesSummaries.unusedTaxExcluded.toLocaleString()}
                                                <br />({sumSalesSummaries.unusedTaxExcluded})
                                            </td>
                                            <td>
                                                {sumSalesSummaries.refundCount.toLocaleString() !== '0' &&
                                                    sumSalesSummaries.refundCount.toLocaleString()}
                                            </td>
                                            <td>
                                                {sumSalesSummaries.refundAmount.toLocaleString() !== '0' &&
                                                    sumSalesSummaries.refundAmount.toLocaleString()}
                                            </td>
                                            <td>
                                                {sumSalesSummaries.refundTaxExcluded.toLocaleString() !== '0' &&
                                                    sumSalesSummaries.refundTaxExcluded.toLocaleString()}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <div className="couponUsageSummaries results">
                            <div className="count-item">[덴탈잡 발급쿠폰 일계 - 발급일(조회일) 기준입니다.]</div>
                            <table className="table-gray table-statis table-daily-registration">
                                <thead>
                                    <tr>
                                        <th>쿠폰명</th>
                                        <th>총발급</th>
                                        <th>총사용가능</th>
                                        <th>당일발급,당일사용</th>
                                        <th className="blue">당일총사용</th>
                                        <th>당일사용,당일발송</th>
                                        <th>당일총발송</th>
                                        <th>당일발급,당일미사용</th>
                                        <th>당일총미사용</th>
                                        <th>당일총소멸(삭제포함)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {couponUsageSummaries && couponUsageSummaries.length > 0 ? (
                                        couponUsageSummaries.map((item, index) => {
                                            return (
                                                <tr key={`couponUsageSummaries${index}`}>
                                                    <td>{item.couponName}</td>
                                                    <td>{item.totalIssued}</td>
                                                    <td>{item.totalUsable}</td>
                                                    <td>{item.issuedUsedToday}</td>
                                                    <td className="blue">{item.totalUsedToday}</td>
                                                    <td>{item.usedSentToday}</td>
                                                    <td>{item.totalSentToday}</td>
                                                    <td>{item.issuedUnusedToday}</td>
                                                    <td>{item.totalUnusedToday}</td>
                                                    <td>{item.expiredToday}</td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <></>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="couponUsageSummaries results">
                            <div className="count-item">[ 덴탈잡 발급쿠폰 월계 ]</div>
                            <table className="table-gray table-statis table-daily-registration">
                                <thead>
                                    <tr>
                                        <th>쿠폰명</th>
                                        <th>총발급</th>
                                        <th>총사용가능</th>
                                        <th className="blue">총사용</th>
                                        <th>총발송</th>
                                        <th>총미사용</th>
                                        <th>총소멸(삭제포함)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {couponSummaries && couponSummaries.length > 0 ? (
                                        couponSummaries.map((item, index) => {
                                            return (
                                                <tr key={`couponSummaries${index}`}>
                                                    <td>{item.couponName}</td>
                                                    <td>{item.totalIssued}</td>
                                                    <td>{item.totalUsable}</td>
                                                    <td className="blue">{item.totalUsed}</td>
                                                    <td>{item.totalSent}</td>
                                                    <td>{item.totalUnused}</td>
                                                    <td>{item.totalExpired}</td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <></>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="paymentSummaries results">
                            <div className="count-item">
                                [덴탈알바 - 이용권 결제내역은 구매일이 아닌 사용일(조회일) 기준입니다.]
                            </div>
                            <table className="table-gray table-statis table-daily-registration">
                                <thead>
                                    <tr>
                                        <th style={productNameStyle}>상품명</th>
                                        <th>단가</th>
                                        <th>이니시스결제</th>
                                        <th>이니시스결제금액</th>
                                        <th>이니시스부가세별도</th>
                                        <th>이용권결제</th>
                                        <th>이용권결제금액</th>
                                        <th>이용권부가세별도</th>
                                        <th className="blue">결제합계</th>
                                        <th>결제합계금액</th>
                                        <th className="blue">결제합계부가세별도</th>
                                        <th>입금일</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paymentSummaries &&
                                        paymentSummaries.length > 0 &&
                                        filterZeroRows(paymentSummaries, PAYMENT_KEYS).map((item, index) => {
                                            return (
                                                <tr
                                                    key={`paymentSummaries${index}`}
                                                    className={item.productName.includes('(합계)') ? 'sum-row' : ''}
                                                >
                                                    <td>{item.productName}</td>
                                                    <td>{item.unitPrice > 0 ? item.unitPrice.toLocaleString() : (!item.productName?.includes('(합계)') ? '0' : '')}</td>

                                                    <td>
                                                        {item.inicisPayment.toLocaleString() !== '0' &&
                                                            item.inicisPayment.toLocaleString()}
                                                    </td>
                                                    <td>
                                                        {item.inicisPaymentAmount.toLocaleString() !== '0' &&
                                                            item.inicisPaymentAmount.toLocaleString()}
                                                    </td>
                                                    <td>
                                                        {item.inicisVATExcluded.toLocaleString() !== '0' &&
                                                            item.inicisVATExcluded.toLocaleString()}
                                                    </td>
                                                    <td>
                                                        {item.ticketPayment.toLocaleString() !== '0' &&
                                                            item.ticketPayment.toLocaleString()}
                                                    </td>
                                                    <td>
                                                        {item.ticketPaymentAmount.toLocaleString() !== '0' &&
                                                            item.ticketPaymentAmount.toLocaleString()}
                                                    </td>
                                                    <td>
                                                        {item.ticketVATExcluded.toLocaleString() !== '0' &&
                                                            item.ticketVATExcluded.toLocaleString()}
                                                    </td>
                                                    <td className={item.productName.includes('(합계)') ? 'blue' : ''}>
                                                        {item.totalPayment.toLocaleString() !== '0' &&
                                                            item.totalPayment.toLocaleString()}
                                                    </td>
                                                    <td>
                                                        {item.totalPaymentAmount.toLocaleString() !== '0' &&
                                                            item.totalPaymentAmount.toLocaleString()}
                                                    </td>
                                                    <td className={item.productName.includes('(합계)') ? 'blue' : ''}>
                                                        {item.totalPaymentVATExcluded.toLocaleString() !== '0' &&
                                                            item.totalPaymentVATExcluded.toLocaleString()}
                                                    </td>
                                                    <td>{item.depositDate}</td>
                                                </tr>
                                            );
                                        })}
                                    {paymentSummaries && paymentSummaries.length > 0 && sumPaymentSummaries && (
                                        <tr className="sum-all">
                                            <td></td>
                                            <td></td>
                                            <td>
                                                {sumPaymentSummaries.inicisPayment.toLocaleString() !== '0' &&
                                                    sumPaymentSummaries.inicisPayment.toLocaleString()}
                                            </td>
                                            <td>
                                                {sumPaymentSummaries.inicisPaymentAmount.toLocaleString() !== '0' &&
                                                    sumPaymentSummaries.inicisPaymentAmount.toLocaleString()}
                                            </td>
                                            <td>
                                                {sumPaymentSummaries.inicisVATExcluded.toLocaleString() !== '0' &&
                                                    sumPaymentSummaries.inicisVATExcluded.toLocaleString()}
                                            </td>
                                            <td>
                                                {sumPaymentSummaries.ticketPayment.toLocaleString() !== '0' &&
                                                    sumPaymentSummaries.ticketPayment.toLocaleString()}
                                            </td>
                                            <td>
                                                {sumPaymentSummaries.ticketPaymentAmount.toLocaleString() !== '0' &&
                                                    sumPaymentSummaries.ticketPaymentAmount.toLocaleString()}
                                            </td>
                                            <td>
                                                {sumPaymentSummaries.ticketVATExcluded.toLocaleString() !== '0' &&
                                                    sumPaymentSummaries.ticketVATExcluded.toLocaleString()}
                                            </td>
                                            <td className="blue">
                                                {sumPaymentSummaries.totalPayment.toLocaleString() !== '0' &&
                                                    sumPaymentSummaries.totalPayment.toLocaleString()}
                                            </td>
                                            <td>
                                                {sumPaymentSummaries.totalPaymentAmount.toLocaleString() !== '0' &&
                                                    sumPaymentSummaries.totalPaymentAmount.toLocaleString()}
                                            </td>
                                            <td className="blue">
                                                {sumPaymentSummaries.totalPaymentVATExcluded.toLocaleString() !== '0' &&
                                                    sumPaymentSummaries.totalPaymentVATExcluded.toLocaleString()}
                                            </td>
                                            <td></td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MAJAB010;
